import { type LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { type LinearConfiguration } from "~/features/configurator/Linear/models";
import { getCsrfValue } from "~/common/utils/ApiHelper";
import { type LinearConfigurationDTO } from "~/features/configurator/Linear/dtos";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { transform } from "~/features/configurator/Linear/linearDataTransformer";
import {
  type ConfiguratorApiCall,
  prepareCommonConfiguratorData,
  type RailConfiguratorApiData,
} from "~/features/configurator/common/commonConfiguratorApi";
import { ConfiguratorType, getConfiguratorFamilyFromProductCode } from "~/domains/configurator/types/ConfiguratorType";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import instance from "~/utils/i18n";
import { XML_TRANSLATION_SHEETS } from "~/features/configurator/common/useXmlConfiguratorTranslations";

type RailConfiguratorApiCall = ConfiguratorApiCall & Required<Pick<ConfiguratorApiCall, "railsConfiguratorData">>;

export function prepareCommonRailData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): RailConfiguratorApiCall {
  const data = prepareCommonConfiguratorData(linearState, priceInfo);

  const configuratorFamily = getConfiguratorFamilyFromProductCode(linearState.productCode);
  const t = (key: string) =>
    instance.t(key, { ns: XML_TRANSLATION_SHEETS[linearState.type!].sheetName, context: configuratorFamily });

  const railData: RailConfiguratorApiData = {
    // currentStepNumber is 1 based
    currentStepNumber: linearState.currentStep + 1,
    highestVisitedStepNumber: linearState.highTideStep + 1,
    customSegmentLengthSelected: linearState.customSegmentLengthSelected,

    description: linearState.description,
    mediaUrl: t(linearState.productImageUrl),
  };

  data.railsConfiguratorData = railData;

  return data as RailConfiguratorApiCall;
}

const apiUrl = resolveApiUrl("/rails/getconfig");

export function sendRail(data: ConfiguratorApiCall): Promise<LinearConfiguration> {
  return fetch(apiUrl, {
    body: JSON.stringify(data),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  }).then((result) => {
    if (result.ok) {
      return result.json().then((data: LinearConfigurationDTO) => transform(data, ConfiguratorType.Rail, undefined));
    } else {
      console.error(`Error sending rail update: ${result.status}`);
      throw new Error(result.status + "");
    }
  });
}
